import { Box, Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type { ReactNode } from "react";
import type { TipoRnp } from "../services/oece.service";

/* ─── Paleta de la vista OECE ─── */
export const C = {
  navy: "#0b1a33",
  navy2: "#16305a",
  gold: "#c8a24a",
  goldSoft: "#f6efdc",
  bg: "#f4f6fa",
  border: "#e3e8f0",
  muted: "#5b6b82",
  red: "#d64545",
  redSoft: "#fdecec",
  green: "#23925f",
  greenSoft: "#e6f5ee",
  amber: "#c98a00",
  amberSoft: "#fff4d6",
  blue: "#2f6fd6",
  blueSoft: "#e8f0fd",
};

/* ─── Formatos ─── */
export const num = (n: number | null | undefined) =>
  n === null || n === undefined ? "—" : n.toLocaleString("es-PE");

export function fechaHora(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-PE", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

export function fecha(iso: string | null | undefined) {
  if (!iso) return "—";
  // Las fechas de OECE vienen como "dd/mm/yyyy"; las nuestras en ISO
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(iso)) return iso;
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

export function haceCuanto(iso: string | null | undefined) {
  if (!iso) return "";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
}

/* ─── Piezas ─── */
type Tono = "red" | "green" | "amber" | "blue" | "gray" | "gold";

const TONOS: Record<Tono, { bg: string; fg: string }> = {
  red: { bg: C.redSoft, fg: C.red },
  green: { bg: C.greenSoft, fg: C.green },
  amber: { bg: C.amberSoft, fg: C.amber },
  blue: { bg: C.blueSoft, fg: C.blue },
  gray: { bg: "#eef1f5", fg: C.muted },
  gold: { bg: C.goldSoft, fg: "#8a6a1c" },
};

export const Pill = ({ tono, children, icon }: { tono: Tono; children: ReactNode; icon?: ReactNode }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex", alignItems: "center", gap: 0.5,
      px: 1, py: 0.25, borderRadius: 999,
      fontSize: 12, fontWeight: 600, lineHeight: 1.6, whiteSpace: "nowrap",
      bgcolor: TONOS[tono].bg, color: TONOS[tono].fg,
      "& svg": { fontSize: 14 },
    }}
  >
    {icon}
    {children}
  </Box>
);

export const EstadoProveedor = ({
  habilitado, sancionado,
}: { habilitado: boolean | null; sancionado: boolean }) => {
  if (sancionado) return <Pill tono="red">Sancionado</Pill>;
  if (habilitado === false) return <Pill tono="amber">No habilitado</Pill>;
  if (habilitado) return <Pill tono="green">Habilitado</Pill>;
  return <Pill tono="gray">Sin ficha</Pill>;
};

const INICIAL_TIPO: Record<string, string> = { "1": "B", "2": "S", "3": "CO", "4": "EO" };

export const TiposRnp = ({ tipos }: { tipos: TipoRnp[] }) =>
  tipos.length === 0 ? (
    <Typography variant="caption" color="text.disabled">Sin RNP vigente</Typography>
  ) : (
    <Stack direction="row" spacing={0.5}>
      {tipos.map((t) => (
        <Tooltip key={t.codigo} title={t.nombre}>
          <Chip
            label={INICIAL_TIPO[t.codigo] ?? t.codigo}
            size="small"
            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: C.blueSoft, color: C.blue }}
          />
        </Tooltip>
      ))}
    </Stack>
  );

export const Tarjeta = ({ children, sx }: { children: ReactNode; sx?: object }) => (
  <Paper
    elevation={0}
    sx={{ border: `1px solid ${C.border}`, borderRadius: 3, bgcolor: "#fff", ...sx }}
  >
    {children}
  </Paper>
);

export const Vacio = ({ texto }: { texto: string }) => (
  <Box sx={{ py: 6, textAlign: "center", color: C.muted }}>
    <Typography variant="body2">{texto}</Typography>
  </Box>
);

/* Estilo común de los DataGrid de la vista */
export const gridSx = {
  border: 0,
  "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8fafc" },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 700, fontSize: 12, textTransform: "uppercase",
    letterSpacing: 0.4, color: C.muted,
  },
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiDataGrid-row:hover": { bgcolor: "#f6f8fc" },
  "& .MuiDataGrid-cell": { display: "flex", alignItems: "center", borderColor: C.border, whiteSpace: "normal" },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
};
